import React, { useState, useMemo } from 'react';
import { 
  ChevronDown, 
  ChevronRight,
  Calendar,
  Users,
  Search,
  Timer,
  ArrowRight
} from 'lucide-react';

const MONTHS = [
  'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
  'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'
];

// Data structure
const PLANNING_DATA = {
  metadata: {
    title: "Planning de Développement",
    version: "1.0.0",
    startDate: new Date('2024-04-01'),
    endDate: new Date('2024-12-31')
  },
  modules: [
    {
      id: "auth",
      name: "Module Authentification",
      description: "Système d'authentification et gestion des utilisateurs",
      startDate: new Date('2024-04-01'),
      endDate: new Date('2024-05-15'),
      status: "in_progress",
      owner: "Équipe Sécurité",
      tasks: [
        {
          id: "auth-spec",
          name: "Spécifications techniques",
          phase: "conception",
          startDate: new Date('2024-04-01'),
          endDate: new Date('2024-04-07'),
          status: "completed",
          progress: 100,
          assignee: "Architecte"
        },
        {
          id: "auth-mockups",
          name: "Maquettes interfaces",
          phase: "conception",
          startDate: new Date('2024-04-08'),
          endDate: new Date('2024-04-14'),
          status: "completed",
          progress: 100,
          assignee: "UX Designer"
        },
        {
          id: "auth-ui",
          name: "Interface connexion",
          phase: "frontend",
          startDate: new Date('2024-04-15'),
          endDate: new Date('2024-04-28'),
          status: "in_progress",
          progress: 60,
          assignee: "Équipe Frontend"
        },
        {
          id: "auth-profile",
          name: "Gestion profil",
          phase: "frontend",
          startDate: new Date('2024-04-29'),
          endDate: new Date('2024-05-05'),
          status: "planned",
          progress: 0,
          assignee: "Équipe Frontend"
        },
        {
          id: "auth-api",
          name: "API Authentification",
          phase: "backend",
          startDate: new Date('2024-04-15'),
          endDate: new Date('2024-04-28'),
          status: "in_progress",
          progress: 40,
          assignee: "Équipe Backend"
        },
        {
          id: "auth-db",
          name: "Modèles de données",
          phase: "backend",
          startDate: new Date('2024-04-08'),
          endDate: new Date('2024-04-14'),
          status: "completed",
          progress: 100,
          assignee: "Équipe Backend"
        }
      ]
    },
    {
      id: "dashboard",
      name: "Module Tableau de Bord",
      description: "Interface principale avec widgets personnalisables",
      startDate: new Date('2024-05-15'),
      endDate: new Date('2024-07-01'),
      status: "planned",
      owner: "Équipe Frontend",
      tasks: [
        {
          id: "dash-spec",
          name: "Spécifications techniques",
          phase: "conception",
          startDate: new Date('2024-05-15'),
          endDate: new Date('2024-05-28'),
          status: "planned",
          progress: 0,
          assignee: "Architecte"
        },
        {
          id: "dash-layout",
          name: "Layout principal",
          phase: "frontend",
          startDate: new Date('2024-05-29'),
          endDate: new Date('2024-06-11'),
          status: "planned",
          progress: 0,
          assignee: "Équipe Frontend"
        }
      ]
    }
  ]
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    completed: { color: 'bg-green-100 text-green-800', label: 'Terminé' },
    in_progress: { color: 'bg-blue-100 text-blue-800', label: 'En cours' },
    planned: { color: 'bg-gray-100 text-gray-800', label: 'Planifié' },
    delayed: { color: 'bg-red-100 text-red-800', label: 'Retardé' }
  };

  const config = statusConfig[status] || statusConfig.planned;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

const getMonthsBetweenDates = (startDate, endDate) => {
  const months = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    months.push(new Date(currentDate));
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return months;
};

const getDaysBetweenDates = (startDate, endDate) => {
  const days = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return days;
};

const GanttTask = ({ task, startDate, endDate, cellWidth }) => {
  const totalDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
  const taskStart = (task.startDate - startDate) / (1000 * 60 * 60 * 24);
  const taskDuration = (task.endDate - task.startDate) / (1000 * 60 * 60 * 24);
  
  const left = `${(taskStart / totalDays) * 100}%`;
  const width = `${(taskDuration / totalDays) * 100}%`;

  const statusColors = {
    completed: 'bg-green-500',
    in_progress: 'bg-blue-500',
    planned: 'bg-gray-400',
    delayed: 'bg-red-500'
  };

  return (
    <div 
      className="absolute h-6 rounded transition-all group cursor-pointer"
      style={{ 
        left, 
        width,
        top: '50%',
        transform: 'translateY(-50%)'
      }}
    >
      <div 
        className={`w-full h-full rounded ${statusColors[task.status]} relative`}
        style={{
          opacity: task.progress < 100 ? 0.7 : 1
        }}
      >
        {task.progress > 0 && task.progress < 100 && (
          <div 
            className="absolute top-0 left-0 h-full bg-opacity-50 bg-white rounded"
            style={{ width: `${100 - task.progress}%`, right: 0 }}
          />
        )}
        
        <div className="hidden group-hover:block absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap mt-px">
          {task.name} ({task.progress}%)
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
            <div className="w-2 h-2 bg-gray-900 transform rotate-45" />
          </div>
        </div>
      </div>
    </div>
  );
};

const GanttChart = ({ data }) => {
  const startDate = data.metadata.startDate;
  const endDate = data.metadata.endDate;
  
  const months = useMemo(() => getMonthsBetweenDates(startDate, endDate), [startDate, endDate]);
  const days = useMemo(() => getDaysBetweenDates(startDate, endDate), [startDate, endDate]);
  const cellWidth = 25; // px par jour

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: days.length * cellWidth }}>
        {/* Header avec les mois et jours */}
        <div className="sticky top-0 bg-white z-10">
          {/* Mois */}
          <div className="flex border-b">
            {months.map((month, index) => {
              const daysInMonth = new Date(
                month.getFullYear(),
                month.getMonth() + 1,
                0
              ).getDate();
              
              return (
                <div
                  key={index}
                  className="border-r text-center text-sm font-medium text-gray-700 bg-gray-50"
                  style={{ width: `${daysInMonth * cellWidth}px` }}
                >
                  {MONTHS[month.getMonth()]} {month.getFullYear()}
                </div>
              );
            })}
          </div>
          
          {/* Jours */}
          <div className="flex border-b">
            {days.map((day, index) => (
              <div
                key={index}
                className={`text-center text-xs text-gray-500 ${
                  day.getDay() === 0 || day.getDay() === 6 
                    ? 'bg-gray-50' 
                    : 'bg-white'
                }`}
                style={{ width: `${cellWidth}px` }}
              >
                {day.getDate()}
              </div>
            ))}
          </div>
        </div>

        {/* Corps du Gantt */}
        <div>
          {data.modules.map(module => (
            <div key={module.id} className="border-b">
              {/* En-tête du module */}
              <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h3 className="text-sm font-medium text-gray-900">
                    {module.name}
                  </h3>
                  <StatusBadge status={module.status} />
                </div>
                <div className="text-xs text-gray-500">
                  {module.owner}
                </div>
              </div>

              {/* Tâches du module */}
              <div>
                {module.tasks.map(task => (
                  <div 
                    key={task.id} 
                    className="border-t h-12 relative flex items-center"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-48 bg-white border-r px-4 flex items-center">
                      <span className="text-sm text-gray-600 truncate">
                        {task.name}
                      </span>
                    </div>
                    <div className="relative ml-48 h-full w-full">
                      <GanttTask
                        task={task}
                        startDate={startDate}
                        endDate={endDate}
                        cellWidth={cellWidth}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const GanttPlanning = () => {
  return (
    <div className="h-full bg-white">
      {/* En-tête */}
      <div className="px-6 py-4 border-b">
        <h1 className="text-xl font-semibold text-gray-900">
          {PLANNING_DATA.metadata.title}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Version {PLANNING_DATA.metadata.version}
        </p>
      </div>

      {/* Diagramme de Gantt */}
      <div className="p-6">
        <GanttChart data={PLANNING_DATA} />
      </div>
    </div>
  );
};

export default GanttPlanning;